import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { MobileNav } from '../components/MobileNav';
import { Coins, Sparkles, ArrowLeft, Gift, TrendingUp, X } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config';

const playfairFont = "'Playfair Display', serif";

const rarityColors = {
  common: { bg: 'rgba(163, 184, 172, 0.2)', border: 'rgba(163, 184, 172, 0.5)', text: '#A3B8AC' },
  rare: { bg: 'rgba(59, 130, 246, 0.2)', border: 'rgba(59, 130, 246, 0.5)', text: '#3B82F6' },
  epic: { bg: 'rgba(168, 85, 247, 0.2)', border: 'rgba(168, 85, 247, 0.5)', text: '#A855F7' },
  legendary: { bg: 'rgba(212, 175, 55, 0.2)', border: 'rgba(212, 175, 55, 0.5)', text: '#D4AF37' }
};

export const LuckyDraw = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawing, setDrawing] = useState(false);
  const [result, setResult] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [coinBalance, setCoinBalance] = useState(0);

  useEffect(() => {
    fetchCampaigns();
    fetchCoinBalance();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const { data } = await axios.get(`${API}/lucky-draws`);
      setCampaigns(data);
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCoinBalance = async () => {
    try {
      const { data } = await axios.get(`${API}/wallet/balance`, { withCredentials: true });
      setCoinBalance(data.coin_balance);
    } catch (error) {
      console.error('Failed to fetch balance:', error);
    }
  };

  const handleDraw = async (campaignId) => {
    setDrawing(true);
    setResult(null);
    setShowResult(false);

    try {
      const { data } = await axios.post(
        `${API}/lucky-draws/${campaignId}/draw`,
        {},
        { withCredentials: true }
      );

      // Animation delay
      await new Promise(resolve => setTimeout(resolve, 2000));

      setResult(data.prize);
      setShowResult(true);
      fetchCoinBalance();
      toast.success('Congratulations! Check your inventory');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Draw failed');
    } finally {
      setDrawing(false);
    }
  };

  const formatCoins = (coins) => {
    return new Intl.NumberFormat('id-ID').format(coins);
  };

  return (
    <div className="min-h-screen pb-24" style={{ background: '#080C0A', fontFamily: "'Outfit', sans-serif" }}>
      <header className="sticky top-0 z-50 backdrop-blur-2xl" style={{ background: 'rgba(8, 12, 10, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="hover:opacity-80">
              <ArrowLeft className="w-6 h-6" style={{ color: '#F8FAF9' }} />
            </button>
            <div>
              <h1 className="text-2xl font-light" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Lucky Draw</h1>
            </div>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: 'rgba(212, 175, 55, 0.2)', border: '1px solid rgba(212, 175, 55, 0.3)' }}>
            <Coins className="w-5 h-5" style={{ color: '#D4AF37' }} />
            <span className="font-semibold" style={{ color: '#D4AF37' }}>{formatCoins(coinBalance)}</span>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block w-12 h-12 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D4AF37', borderTopColor: 'transparent' }} />
          </div>
        ) : campaigns.length === 0 ? (
          <div className="text-center py-20 rounded-2xl backdrop-blur-xl" style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <Gift className="w-16 h-16 mx-auto mb-4" style={{ color: '#A3B8AC' }} />
            <p style={{ color: '#A3B8AC' }}>No active campaigns</p>
          </div>
        ) : (
          <div className="space-y-8">
            {campaigns.map((campaign) => (
              <motion.div
                key={campaign.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-2xl p-8 backdrop-blur-xl"
                style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.1)' }}
              >
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-3xl font-light mb-2" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>
                      {campaign.name}
                    </h2>
                    <p className="mb-4" style={{ color: '#A3B8AC' }}>{campaign.description}</p>
                    <div className="flex items-center gap-3">
                      <div className="px-4 py-2 rounded-full" style={{ background: 'rgba(212, 175, 55, 0.2)', border: '1px solid rgba(212, 175, 55, 0.3)' }}>
                        <span className="font-semibold" style={{ color: '#D4AF37' }}>{formatCoins(campaign.coin_cost)} Coins/Draw</span>
                      </div>
                    </div>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleDraw(campaign.id)}
                    disabled={drawing || coinBalance < campaign.coin_cost}
                    className="px-8 py-3 rounded-full font-semibold transition-all disabled:opacity-50 flex items-center gap-2"
                    style={{ background: '#D4AF37', color: '#080C0A' }}
                  >
                    <Sparkles className="w-5 h-5" />
                    Draw Now
                  </motion.button>
                </div>

                {/* Prizes */}
                <div>
                  <h3 className="text-lg font-medium mb-4" style={{ color: '#F8FAF9' }}>Available Prizes:</h3>
                  <div className="grid md:grid-cols-4 gap-4">
                    {campaign.prizes?.map((prize) => {
                      const rarity = rarityColors[prize.rarity] || rarityColors.common;
                      return (
                        <div
                          key={prize.id}
                          className="rounded-xl p-4 backdrop-blur-xl"
                          style={{ background: rarity.bg, border: `1px solid ${rarity.border}` }}
                        >
                          <img src={prize.image_url} alt={prize.name} className="w-full h-32 object-cover rounded-lg mb-3" onError={(e) => { e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"%3E%3Crect width="200" height="200" fill="%23080C0A"/%3E%3Ctext x="100" y="100" text-anchor="middle" fill="%23D4AF37" font-size="14"%3ENo Image%3C/text%3E%3C/svg%3E'; }} />
                          <p className="font-medium text-sm mb-1" style={{ color: '#F8FAF9' }}>{prize.name}</p>
                          <p className="text-xs mb-2" style={{ color: rarity.text }}>{prize.rarity.toUpperCase()}</p>
                          <p className="text-xs" style={{ color: '#A3B8AC' }}>Stock: {prize.stock}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Drawing Animation */}
      <AnimatePresence>
        {drawing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{ background: 'rgba(8, 12, 10, 0.95)' }}
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            >
              <Sparkles className="w-24 h-24" style={{ color: '#D4AF37' }} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Result Modal */}
      <AnimatePresence>
        {showResult && result && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-6"
            style={{ background: 'rgba(8, 12, 10, 0.95)' }}
            onClick={() => setShowResult(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="max-w-md w-full rounded-2xl p-8 backdrop-blur-xl relative"
              style={{ background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(212, 175, 55, 0.5)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowResult(false)}
                className="absolute top-4 right-4"
                style={{ color: '#A3B8AC' }}
              >
                <X className="w-6 h-6" />
              </button>

              <div className="text-center">
                <Sparkles className="w-16 h-16 mx-auto mb-4" style={{ color: '#D4AF37' }} />
                <h2 className="text-3xl font-light mb-4" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>
                  Congratulations!
                </h2>
                <img src={result.image_url} alt={result.name} className="w-full h-48 object-cover rounded-xl mb-4" />
                <p className="text-xl font-semibold mb-2" style={{ color: '#F8FAF9' }}>{result.name}</p>
                <p className="text-sm mb-6" style={{ color: rarityColors[result.rarity]?.text || '#A3B8AC' }}>
                  {result.rarity.toUpperCase()}
                </p>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/inventory')}
                  className="w-full py-3 rounded-full font-semibold"
                  style={{ background: '#D4AF37', color: '#080C0A' }}
                >
                  View Inventory
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <MobileNav />
    </div>
  );
};
